import { ref } from 'vue-demi';
import type { Ref } from 'vue-demi';
import { isApiBusinessError, isApiServerError } from '@ark/web-api/frontend';
import type { AmoeApiClient } from '../shared/api';
import { getAmoeBusinessCode, getAmoeErrorRouting } from '../shared/error-map';
import t from '../render/utils/i18n';

export type AvailabilityReason =
  | 'not_started'
  | 'ended'
  | 'no_round'
  | undefined;

export type UseAvailabilityReturn = {
  hasActiveRound: Ref<boolean>;
  reason: Ref<AvailabilityReason>;
  loading: Ref<boolean>;
  error: Ref<string>;
  /** D-34: 由 render onMounted 显式调,不自动拉 */
  fetch(seasonSign: string): Promise<void>;
};

export function useAvailability(
  apiClient: AmoeApiClient,
): UseAvailabilityReturn {
  const hasActiveRound = ref(false);
  const reason = ref<AvailabilityReason>(undefined);
  const loading = ref(false);
  const error = ref('');

  async function fetch(seasonSign: string): Promise<void> {
    if (seasonSign.length === 0) {
      hasActiveRound.value = false;
      reason.value = 'no_round';
      return;
    }
    loading.value = true;
    error.value = '';
    try {
      const data = await apiClient.getAvailability(seasonSign);
      hasActiveRound.value = data.hasActiveRound;
      reason.value = data.reason;
    } catch (err) {
      const businessCode = getAmoeBusinessCode(err);
      if (businessCode !== undefined || isApiBusinessError(err)) {
        const code =
          businessCode ?? (err as { businessCode: number }).businessCode;
        // RH-B5: getAmoeErrorRouting 只收 businessCode + 独立 reason,不读业务文案
        const routing = getAmoeErrorRouting(code, undefined);
        if (routing.kind === 'noRound') {
          hasActiveRound.value = false;
          reason.value = routing.reason;
        } else if (routing.kind === 'toast') {
          error.value = routing.message;
        } else {
          error.value = t('amoe.errors.network');
        }
      } else if (isApiServerError(err)) {
        error.value = t('amoe.errors.network');
      } else {
        error.value = t('amoe.errors.network');
      }
      hasActiveRound.value = false;
    } finally {
      loading.value = false;
    }
  }

  return { hasActiveRound, reason, loading, error, fetch };
}
